import { useCallback, useMemo } from 'react';
import { useContactBook } from '../../../lib/contact-book';
import { contactDisplayName } from '../../../lib/contact-rows';
import { useTranslation } from '../../../lib/i18n';
import { stageGroup, threadByOutreachId, threadStage, type StageGroupId } from '../../../lib/outreach-stage';
import { leadDisplayName, leadRowStatus, type PeopleLeadRow } from '../../../lib/profile-links-model';
import { useProfileLinkLeadRows } from '../profile-links/ProfileLinkLeadsSection';

/** What one outreach card knows about its thread beyond the sent summary: the person, the lead, the actions' ids. */
export interface OutreachCardContext {
  /** The contact book's name for them ('' when the book names nobody). */
  contactName: string;
  personId?: string;
  recipientId?: string;
  stageGroup?: StageGroupId;
  /** A profile-link visitor: their name or "Visitor from <link>", the link they came through, the owner-side chat. */
  lead?: { name: string; linkLabel: string; needsYou: boolean; conversationId?: string };
}

/**
 * Joins a thread to the contact book (person, stage) and to the owner's profile-link leads (who the visitor is,
 * whether they need the owner, the owner-side conversation). Both reads are cache entries the app already warms.
 */
export function useOutreachCardContext(): (outreachId: string | undefined) => OutreachCardContext {
  const { t } = useTranslation();
  const book = useContactBook('');
  const leads = useProfileLinkLeadRows();
  const leadByOutreach = useMemo(() => {
    const map = new Map<string, PeopleLeadRow>();
    for (const row of leads) {
      const id = row.lead.outreachId?.trim();
      if (id && !map.has(id)) map.set(id, row);
    }
    return map;
  }, [leads]);
  const contacts = book.kind === 'ready' ? book.contacts : undefined;
  return useCallback(
    (outreachId) => {
      const joined = threadByOutreachId(contacts, outreachId);
      const row = outreachId ? leadByOutreach.get(outreachId) : undefined;
      const personId = joined?.entry.contact?.personId || undefined;
      const recipientId = joined?.entry.recipientId || undefined;
      return {
        contactName: joined ? contactDisplayName(joined.entry.contact, joined.entry.channels, '') : '',
        ...(personId ? { personId } : {}),
        ...(recipientId ? { recipientId } : {}),
        ...(joined ? { stageGroup: stageGroup(threadStage(joined.thread)) } : {}),
        ...(row
          ? {
              lead: {
                name: leadDisplayName(row, (link) => t('profileLinks.leads.visitorFrom', { link }), t('profileLinks.main')),
                linkLabel: row.linkLabel ?? t('profileLinks.main'),
                needsYou: leadRowStatus(row.lead).needsYou,
                ...(row.lead.conversationId ? { conversationId: row.lead.conversationId } : {}),
              },
            }
          : {}),
      };
    },
    [contacts, leadByOutreach, t],
  );
}
